import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CandidateCard = ({ candidate, onUnlock, onAddToShortlist, isInShortlist }) => {
  const navigate = useNavigate();

  const handleUnlockClick = () => {
    navigate('/profile-unlock-payment', { state: { candidateData: candidate } });
  };

  const getExperienceColor = (level) => {
    switch (level) {
      case 'entry': return 'text-blue-600 bg-blue-50';
      case 'mid': return 'text-green-600 bg-green-50';
      case 'senior': return 'text-purple-600 bg-purple-50';
      case 'lead': return 'text-orange-600 bg-orange-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'immediate': return 'text-green-600 bg-green-50';
      case '2weeks': return 'text-blue-600 bg-blue-50';
      case '1month': return 'text-yellow-600 bg-yellow-50';
      case '3months': return 'text-red-600 bg-red-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-lg transition-all duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{candidate?.title}</h3>
            <p className="text-sm text-muted-foreground">
              Anonymous Candidate #{candidate?.id}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="text-right">
            <div className="text-lg font-bold text-primary">{candidate?.matchPercentage}%</div>
            <div className="text-xs text-muted-foreground">Match</div>
          </div>
          <button
            onClick={() => onAddToShortlist(candidate)}
            className={`p-2 rounded-lg transition-colors ${
              isInShortlist 
                ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
            aria-label={isInShortlist ? 'Remove from shortlist' : 'Add to shortlist'}
          >
            <Icon name={isInShortlist ? "BookmarkCheck" : "Bookmark"} size={20} />
          </button>
        </div>
      </div>
      {/* Skills */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-2">Top Skills</h4>
        <div className="flex flex-wrap gap-2">
          {candidate?.skills?.slice(0, 6)?.map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-md"
            >
              {skill}
            </span>
          ))}
          {candidate?.skills?.length > 6 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-md">
              +{candidate?.skills?.length - 6} more
            </span>
          )}
        </div>
      </div>
      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{candidate?.location}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Briefcase" size={16} className="text-muted-foreground" />
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getExperienceColor(candidate?.experienceLevel)}`}>
              {candidate?.experienceLevel === 'entry' && 'Entry Level'}
              {candidate?.experienceLevel === 'mid' && 'Mid Level'}
              {candidate?.experienceLevel === 'senior' && 'Senior Level'}
              {candidate?.experienceLevel === 'lead' && 'Lead/Principal'}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="GraduationCap" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{candidate?.education}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getAvailabilityColor(candidate?.availability)}`}>
              {candidate?.availability === 'immediate' && 'Available now'}
              {candidate?.availability === '2weeks' && '2 weeks'}
              {candidate?.availability === '1month' && '1 month'}
              {candidate?.availability === '3months' && '3 months'}
            </span>
          </div>
        </div>
      </div>
      {/* Languages */}
      {candidate?.languages && candidate?.languages?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Languages" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Languages</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {candidate?.languages?.map((language, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
              >
                {language}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Work Preferences */}
      <div className="flex items-center space-x-4 mb-4 text-sm text-muted-foreground">
        {candidate?.remoteWork && (
          <div className="flex items-center space-x-1">
            <Icon name="Home" size={14} />
            <span>Remote OK</span>
          </div>
        )}
        {candidate?.workPermit && (
          <div className="flex items-center space-x-1">
            <Icon name="FileCheck" size={14} />
            <span>Work Permit</span>
          </div>
        )}
      </div>
      {/* Salary Range (if available) */}
      {candidate?.salaryRange && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="DollarSign" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {candidate?.salaryRange?.min?.toLocaleString('no-NO')} - {candidate?.salaryRange?.max?.toLocaleString('no-NO')} NOK
            </span>
          </div>
        </div>
      )}
      {/* Unlock Section */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            <div className="font-medium text-foreground">Unlock to reveal:</div>
            <div>Name, photo, contact details, portfolio</div>
          </div>
          
          <div className="text-right">
            <div className="text-lg font-bold text-foreground mb-1">149 NOK</div>
            <Button
              onClick={handleUnlockClick}
              size="sm"
              iconName="Unlock"
              iconPosition="left"
            >
              Unlock Profile
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateCard;